import { Fragment, useCallback, useMemo } from 'react'

import { SortableContext } from '@dnd-kit/sortable'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'

import ProductDimensionDraggable from './product-dimension-draggable'
import ProductDimensionOptionsForm from './product-dimension-options-form'
import type { NormalizedDimension } from './schemas'

type Props = {
    isNewProduct: boolean
    normalizedDimensions: NormalizedDimension[]
    existingDimensions: NormalizedDimension[]
    selectedDimension?: NormalizedDimension
    onEdit: (dimension: NormalizedDimension) => void
    onChange: (dimensions: NormalizedDimension[]) => void
    onRemove: (dimensionIndex: number) => void
    onCancel: () => void
}

export default function ProductDimensionDroppable({
    isNewProduct,
    normalizedDimensions,
    selectedDimension,
    existingDimensions,
    onEdit,
    onChange,
    onRemove,
    onCancel,
}: Props) {
    const updateDimensions = useCallback(
        (updatedDimension: NormalizedDimension) => {
            const newDimensions = [...normalizedDimensions]

            const dimensionIndex = newDimensions.findIndex(
                ({ index }) => index === updatedDimension.index
            )
            if (dimensionIndex === -1) {
                return
            }

            newDimensions[dimensionIndex] = updatedDimension

            onChange(newDimensions)
        },
        [normalizedDimensions, onChange]
    )

    const items = useMemo(() => normalizedDimensions.map((d) => d.id), [normalizedDimensions])

    return (
        <Stack gap={2}>
            <SortableContext items={items}>
                {normalizedDimensions.map((dimension) =>
                    selectedDimension?.name === dimension.name &&
                    selectedDimension?.index === dimension.index ? (
                        <Fragment key={dimension.name}>
                            <ProductDimensionOptionsForm
                                isNewProduct={isNewProduct}
                                index={dimension.index}
                                defaultValues={selectedDimension}
                                existingDimensions={existingDimensions}
                                onSubmit={updateDimensions}
                                onRemove={onRemove}
                                onCancel={onCancel}
                            />
                            <Divider />
                        </Fragment>
                    ) : (
                        <ProductDimensionDraggable
                            key={dimension.name}
                            index={dimension.index}
                            dimension={dimension}
                            canReorder={!!selectedDimension}
                            isSingleDimension={normalizedDimensions.length === 1}
                            isNewProduct={isNewProduct}
                            onEdit={onEdit}
                            onRemove={onRemove}
                        />
                    )
                )}
            </SortableContext>
        </Stack>
    )
}
